import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { UserPlus, UserCheck, Coins, Music2, Users } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { SongRow } from "@/components/SongCard";
import { TipModal } from "@/components/TipModal";

export default function PublicProfile() {
  const { username } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [following, setFollowing] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/users/${username}`).then((r) => { setData(r.data); setFollowing(r.data.is_following); }).catch(() => setData(false));
  }, [username]);
  useEffect(() => { load(); }, [load]);

  if (data === null) return <div className="py-24 text-center text-slate-500">Loading…</div>;
  if (data === false) return <div className="py-24 text-center text-slate-500">Creator not found</div>;

  const p = data.profile;
  const isMe = user?.id === p.id;

  const toggleFollow = async () => {
    if (!user) return toast.error("Sign in to follow");
    try {
      if (following) { await api.delete(`/users/${p.id}/follow`); setFollowing(false); }
      else { await api.post(`/users/${p.id}/follow`); setFollowing(true); }
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-4 lg:pt-6 max-w-3xl mx-auto">
      <div className="jx-radial-bloom border border-white/10 rounded-3xl p-6 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 grid place-items-center text-3xl font-bold overflow-hidden shrink-0">
            {p.avatar_url ? <img src={p.avatar_url} className="w-full h-full object-cover" alt="" /> : p.display_name?.[0]}
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold font-display truncate">{p.display_name}</h1>
            <p className="text-sm text-slate-500">@{p.username}</p>
            {p.bio && <p className="text-sm text-slate-400 mt-1">{p.bio}</p>}
          </div>
        </div>
        <div className="flex gap-6 mt-4 text-sm">
          <span className="flex items-center gap-1.5 text-slate-400"><Users size={14} /> <b className="text-slate-200">{p.followers_count}</b> followers</span>
          <span className="flex items-center gap-1.5 text-slate-400"><Music2 size={14} /> <b className="text-slate-200">{data.songs.length}</b> tracks</span>
          <span className="flex items-center gap-1.5 text-slate-400"><Coins size={14} /> <b className="text-slate-200">${Number(data.tips_received).toFixed(2)}</b> tips</span>
        </div>
        {!isMe && (
          <div className="flex gap-2 mt-4">
            <button onClick={toggleFollow} data-testid="follow-btn" className={`flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold ${following ? "bg-white/10 text-white" : "jx-cta text-white"}`}>
              {following ? <><UserCheck size={16} /> Following</> : <><UserPlus size={16} /> Follow</>}
            </button>
            <button onClick={() => setTipOpen(true)} data-testid="profile-tip-btn" className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold border border-white/15 hover:border-purple-500/40"><Coins size={16} /> Tip</button>
          </div>
        )}
      </div>

      <h2 className="font-semibold mb-3">Published music</h2>
      <div className="space-y-1">
        {data.songs.length === 0 && <p className="text-slate-500 text-sm py-4">No public songs yet.</p>}
        {data.songs.map((s) => <SongRow key={s.id} song={{ ...s, creator_name: p.display_name, stream_url: `${api.defaults.baseURL}/stream/${s.id}` }} list={data.songs.map((x) => ({ ...x, creator_name: p.display_name, stream_url: `${api.defaults.baseURL}/stream/${x.id}` }))} />)}
      </div>

      <TipModal open={tipOpen} onClose={() => setTipOpen(false)} recipient={p} onDone={load} />
    </div>
  );
}
